import { useEffect, useRef, useState } from "react";
import MuxPlayer from "@mux/mux-player-react";
import { useServerFn } from "@tanstack/react-start";
import { MinimalPlayerFrame } from "@/components/MinimalPlayerFrame";
import { createCheckoutSession } from "@/lib/stripe.functions";

type AnswerState = number | null;

const Q1_OPTIONS = [
  "A list of every customer the business has",
  "A financial report showing money in, money out, and profit or loss over a period of time",
  "A legal document signed by directors",
  "A forecast of next year's sales",
];
const Q1_CORRECT = 1;

const Q2_OPTIONS = ["Profit", "Tax", "Revenue", "Costs"];
const Q2_CORRECT = 2;

const mono = { fontFamily: '"JetBrains Mono", monospace' } as const;
const serif = { fontFamily: '"Cormorant Garamond", serif' } as const;
const PREVIEW_COMPLETE_KEY = "lesson_complete_1.1";
const PREVIEW_SCORE_KEY = "lesson_score_1.1";
const COMPLETION_KEY = "wk-completed-lessons";

function readPreviewComplete(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (window.localStorage.getItem(PREVIEW_COMPLETE_KEY) === "true") return true;
    const raw = window.localStorage.getItem(COMPLETION_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) && arr.includes("1.1");
  } catch {
    return false;
  }
}

function markPreviewComplete(score: number) {
  try {
    window.localStorage.setItem(PREVIEW_COMPLETE_KEY, "true");
    window.localStorage.setItem(PREVIEW_SCORE_KEY, String(score));
    const raw = window.localStorage.getItem(COMPLETION_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    const set = new Set(Array.isArray(arr) ? arr.filter((x) => typeof x === "string") : []);
    set.add("1.1");
    window.localStorage.setItem(COMPLETION_KEY, JSON.stringify([...set]));
    window.dispatchEvent(new CustomEvent("wk-completion-updated"));
  } catch {}
}

function readPreviewScore(): number | null {
  if (typeof window === "undefined") return null;
  try {
    const value = Number(window.localStorage.getItem(PREVIEW_SCORE_KEY));
    return value >= 0 && value <= 2 ? value : null;
  } catch {
    return null;
  }
}

export function FreePreview() {
  const [showQuiz, setShowQuiz] = useState(false);
  const [whichQuestion, setWhichQuestion] = useState<1 | 2>(1);
  const [a1, setA1] = useState<AnswerState>(null);
  const [a2, setA2] = useState<AnswerState>(null);
  const [showResults, setShowResults] = useState(() => readPreviewComplete());
  const [persistedScore, setPersistedScore] = useState<number | null>(() => readPreviewScore());
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const playerRef = useRef<any>(null);
  const startCheckout = useServerFn(createCheckoutSession);
  // Per-mount nonce — dedupes anonymous double-clicks on this page-load.
  // New mount / reload → new nonce → new Stripe session, as expected.
  const clientNonceRef = useRef<string>(
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  );

  // Native `ended` listener — MuxPlayer's React onEnded prop is unreliable on
  // iOS Safari (especially out of native fullscreen). The underlying <video>
  // element's `ended` event fires consistently across desktop and mobile.
  useEffect(() => {
    if (showQuiz) return;
    let frame = 0;
    let attempts = 0;
    let cleanup: (() => void) | undefined;
    const attach = () => {
      const p = playerRef.current;
      if (!p) {
        if (attempts < 60) {
          attempts += 1;
          frame = requestAnimationFrame(attach);
        }
        return;
      }
      const onEnded = () => {
        setShowQuiz(true);
        try {
          if (typeof document !== "undefined") {
            if (document.fullscreenElement) document.exitFullscreen?.();
            else if ((document as any).webkitFullscreenElement) (document as any).webkitExitFullscreen?.();
          }
        } catch {}
      };
      p.addEventListener?.("ended", onEnded);
      cleanup = () => p.removeEventListener?.("ended", onEnded);
    };
    attach();
    return () => {
      if (frame) cancelAnimationFrame(frame);
      cleanup?.();
    };
  }, [showQuiz]);

  const handleCheckout = async () => {
    if (checkoutLoading) return;
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      const res = await startCheckout({ data: { clientNonce: clientNonceRef.current } });
      if (res.ok) {
        window.location.assign(res.url);
        return;
      }
      if (res.error === "rate_limited") {
        const { formatRateLimitMessage } = await import("@/lib/rate-limit-message");
        setCheckoutError(formatRateLimitMessage(res.retryAfterSeconds));
        return;
      }
      setCheckoutError("Couldn't start checkout. Please try again or email hello@whoknew.guide.");
    } catch {
      setCheckoutError("Couldn't start checkout. Please try again or email hello@whoknew.guide.");
    } finally {
      setCheckoutLoading(false);
    }
  };


  const score = (a1 === Q1_CORRECT ? 1 : 0) + (a2 === Q2_CORRECT ? 1 : 0);
  const resultScore = persistedScore ?? score;


  const renderQuestion = (
    qNum: 1 | 2,
    label: string,
    text: string,
    options: string[],
    correct: number,
    answer: AnswerState,
    setAnswer: (n: number) => void,
    onNext: () => void,
    nextLabel: string,
    correctFeedback: string,
    incorrectFeedback: string,
  ) => {
    const answered = answer !== null;
    return (
      <div>
        <p
          className="text-[11px] font-bold uppercase tracking-[0.05em] text-burgundy"
          style={mono}
        >
          {label}
        </p>
        <h3
          className="mt-3 text-[22px] font-bold leading-[1.3] text-ink"
          style={serif}
        >
          {text}
        </h3>
        <div className="mt-6 space-y-3">
          {options.map((opt, i) => {
            const isCorrect = i === correct;
            const isSelected = answer === i;
            let bg = "bg-paper-dark hover:bg-[#DDD4C2]";
            let opacity = "";
            if (answered) {
              if (isSelected && isCorrect) bg = "bg-burgundy/10";
              else if (isSelected && !isCorrect) bg = "bg-[#5A534A]/10";
              else bg = "bg-paper-dark";
              if (!isSelected && !isCorrect) opacity = "opacity-50";
            }
            return (
              <button
                key={i}
                type="button"
                disabled={answered}
                onClick={() => !answered && setAnswer(i)}
                className={`interactive-lift w-full text-left flex items-center justify-between gap-3 px-5 py-4 md:py-4 min-h-[48px] rounded-none transition-colors ${bg} ${opacity} ${answered ? "cursor-default" : "cursor-pointer"}`}
                style={serif}
              >
                <span className="text-[18px] leading-[1.4] text-ink">{opt}</span>
                {answered && isCorrect && (
                  <span className="text-burgundy text-[18px] font-bold shrink-0">✓</span>
                )}
                {answered && isSelected && !isCorrect && (
                  <span className="text-[#5A534A] text-[18px] shrink-0">×</span>
                )}
              </button>
            );
          })}
        </div>

        {answered && (
          <>
            <div
              className="mt-6 border-l-[4px] border-burgundy pl-3 animate-fade-in"
              style={{ animation: "fadeIn 300ms ease-out" }}
            >
              {answer === correct ? (
                <p className="text-[16px] leading-[1.5] text-ink" style={serif}>
                  <span className="text-burgundy text-[18px] font-bold">✓</span>{" "}
                  {correctFeedback}
                </p>
              ) : (
                <p className="text-[16px] leading-[1.5] text-ink" style={serif}>
                  {incorrectFeedback}
                </p>
              )}
            </div>
            <div className="mt-6">
              <button
                type="button"
                onClick={onNext}
                className="interactive-lift bg-burgundy text-paper px-6 py-3 rounded-none text-[13px] font-bold uppercase tracking-[0.05em] hover:bg-burgundy-2 transition-colors"
                style={mono}
              >
                {nextLabel}
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  const results = (() => {
    if (resultScore === 2)
      return {
        headline: "That was a Lesson 1.1 quiz. There are 28 more like it.",
      };
    if (resultScore === 1)
      return {
        headline: "One you got, one you didn't. The rest of the course makes both stick.",
      };
    return {
      headline: "Genuinely useful information. The next 29 lessons make all of this click.",
    };
  })();

  return (
    <section id="free-preview" className="max-w-[720px] pt-[64px] md:pt-[96px]">
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
      <hr className="w-[60px] border-0 h-[2px] bg-burgundy" />
      <p
        className="mt-6 text-[11px] font-bold uppercase tracking-[0.05em] text-burgundy"
        style={mono}
      >
        Free preview
      </p>
      <h2 className="mt-6 text-[24px] md:text-[32px] font-bold leading-[1.2] text-ink">
        Lesson 1.1 — What a P&amp;L actually is.
      </h2>
      <p
        className="mt-4 text-[18px] italic leading-[1.6]"
        style={{ ...serif, color: "#5A534A" }}
      >
        3 minutes 53 seconds. No signup. No email. Just watch.
      </p>

      <hr className="mt-4 mb-6 w-full border-0 h-px bg-burgundy" />

      {!showQuiz && (
        <MinimalPlayerFrame playerRef={playerRef as any} maxWidth="720px">
          <MuxPlayer
            ref={playerRef as any}
            playbackId="GXGRBFLNHyBswsqLtKCOPLfWPa7bimddirV6v8GMUVM"
            streamType="on-demand"
            preload="metadata"
            poster="https://image.mux.com/GXGRBFLNHyBswsqLtKCOPLfWPa7bimddirV6v8GMUVM/thumbnail.webp"
            metadata={{ video_title: "WHO KNEW · Lesson 1.1 — What is a P&L" }}
            accentColor="#8B1E1E"
            defaultHiddenCaptions
            nohotkeys
            playsInline
            loop={false}
            style={{ width: "100%", height: "100%", display: "block" }}
          />

        </MinimalPlayerFrame>
      )}


      {showResults ? (
        <div ref={resultsRef} className="mt-10" style={{ animation: "fadeIn 400ms ease-out" }}>
          <p className="text-[11px] font-bold uppercase tracking-[0.05em] text-burgundy" style={mono}>
            YOU'RE READY
          </p>
          <h3 className="mt-4 text-[24px] font-bold leading-[1.3] text-ink" style={serif}>
            {results.headline}
          </h3>
          <p className="mt-6 text-[16px] italic" style={{ ...serif, color: "#5A534A" }}>
            The course is £99. Yours forever. 14-day refund if it's not for you.
          </p>
          <div className="mt-6">
            <button
              type="button"
              onClick={handleCheckout}
              disabled={checkoutLoading}
              className="interactive-lift inline-block bg-burgundy text-paper rounded-none text-[16px] font-bold uppercase tracking-[0.05em] hover:bg-burgundy-2 transition-colors disabled:opacity-70 disabled:cursor-not-allowed border-0 cursor-pointer"
              style={{ ...mono, padding: "18px 32px" }}
            >
              {checkoutLoading ? "Starting…" : "Start the course"}
            </button>
            {checkoutError && (
              <p className="mt-3 text-[13px]" style={{ ...serif, color: "#8B1E1E" }}>
                {checkoutError}
              </p>
            )}
          </div>
        </div>
      ) : showQuiz ? (
        <div className="mt-10" style={{ animation: "fadeIn 400ms ease-out" }}>
          <p className="text-[11px] font-bold uppercase tracking-[0.05em] text-burgundy" style={mono}>
            TEST YOURSELF
          </p>

          <div className="mt-8" ref={resultsRef}>
            {whichQuestion === 1 ? (
              renderQuestion(
                1,
                "Question 1 of 2",
                "What is a P&L?",
                Q1_OPTIONS,
                Q1_CORRECT,
                a1,
                (n) => setA1(n),
                () => setWhichQuestion(2),
                "Next question",
                "Correct. A P&L covers a period of time and shows revenue, costs, and profit or loss. Period of time matters. A balance sheet is a snapshot; a P&L is a film.",
                "The answer is the second option. A P&L is a financial report showing money in, money out, and profit or loss over a period of time. A list of customers, a legal document, and a forecast are all real things — but they aren't a P&L.",
              )
            ) : (
              renderQuestion(
                2,
                "Question 2 of 2",
                "Which line sits at the top of every P&L?",
                Q2_OPTIONS,
                Q2_CORRECT,
                a2,
                (n) => setA2(n),
                () => {
                  const finalScore = (a1 === Q1_CORRECT ? 1 : 0) + (a2 === Q2_CORRECT ? 1 : 0);
                  setPersistedScore(finalScore);
                  markPreviewComplete(finalScore);
                  setShowResults(true);
                },
                "See your results",
                "Correct. Revenue sits at the top of every P&L. From a corner café to a FTSE 100 — same shape.",
                "The answer is Revenue. It sits at the top of every P&L — then costs, then profit. Same structure for every business, everywhere. The most important idea in Module 1.",
              )
            )}
          </div>
        </div>
      ) : (
        <p className="mt-4 text-[14px] italic" style={{ ...serif, color: "#5A534A", lineHeight: 1.5 }}>
          Test yourself with the quiz when the video ends.
        </p>
      )}
    </section>
  );
}
